<script setup lang="ts">
import type { AppartementEntry } from "@/.storyblok/types/120421/storyblok-components";

const props = defineProps<{
  blok: AppartementEntry;
}>();

const useThreeColumnsGrid = computed(() => {
  return props.blok.images.length > 2;
});

const appartmentReferences = computed(() => {
  return props.blok.otherAppartements[0].appartements;
});

useSeoMeta({
  title: props.blok.seoTitle,
  description: props.blok.seoDescription,
});
</script>

<template>
  <BaseContentSection background-color="light" class="appartement-container">
    <BaseTwoColumnContainer>
      <template #leftColumn>
        <div class="flex w-full h-full items-start">
          <BaseImageSlider
            :images="blok.images"
            aspect-ratio="1/1"
            :slides-per-view="1"
          ></BaseImageSlider>
        </div>
      </template>
      <template #rightColumn>
        <BaseHeadline
          tag="h1"
          :text="blok.name"
          :emphasize="true"
          class="mb-2 sc-h2 lg:sc-h2-large"
        ></BaseHeadline>
        <p class="mb-4">{{ blok.description }}</p>
        <BaseAppartementIcons
          class="mb-8"
          :has-bathroom="blok.hasBathroom"
          :has-kitchen="blok.hasKitchen"
          :has-wlan="blok.hasWlan"
          :number-of-floors="blok.numberOfFloors"
          :number-of-people="blok.numberOfPeople"
        ></BaseAppartementIcons>
        <p class="font-optimus sc-h3 mb-4 font-normal">{{ blok.price }}</p>
        <BlokButton :blok="blok.button[0]"></BlokButton>
      </template>
    </BaseTwoColumnContainer>
  </BaseContentSection>
  <BaseContentSection background-color="dark"
    ><BaseHeadline
      tag="h3"
      :text="blok.imagesHeadline"
      :emphasize="true"
      class="mb-4 md:mb-8"
    ></BaseHeadline>
    <div
      class="image-grid"
      :class="{
        'image-grid--4/4/4': useThreeColumnsGrid,
      }"
    >
      <BaseImage
        v-for="(image, index) of blok.images"
        :key="image.id"
        v-bind="image"
        aspect-ratio="4/3"
        :isOgImage="index === 0 ? true : false"
      ></BaseImage>
    </div>
  </BaseContentSection>
  <BaseContentSection background-color="light"
    ><BaseHeadline
      tag="h3"
      :text="blok.otherAppartementsHeadline"
      :emphasize="true"
      class="mb-4 md:mb-8"
    ></BaseHeadline>

    <div class="flex flex-col lg:flex-row -my-2 sm:-my-4 lg:-mx-4">
      <div
        v-for="appartement of appartmentReferences"
        :key="appartement.id"
        class="flex-grow w-full my-2 sm:my-4 lg:mx-4"
      >
        <BaseAppartementReference
          :slug="appartement.full_slug"
          :appartement="appartement.content"
        ></BaseAppartementReference>
      </div>
    </div>
  </BaseContentSection>
</template>

<style lang="scss" scoped>
.appartement-container {
  position: relative;
  overflow: hidden;

  @screen lg {
    &:after {
      background-image: url(/images/apartment_deko-min.png);
      background-repeat: no-repeat;
      background-size: cover;
      content: "";
      display: block;
      width: 200px;
      height: 100%;
      position: absolute;
      top: 0;
      right: 0;
      transform: translateX(50%);
    }
  }
}
.image-grid {
  @apply mx-auto grid gap-4 justify-center;

  grid-template-columns: 1fr;
  grid-template-rows: auto;

  @screen xs {
    grid-template-columns: repeat(2, minmax(0, 50%));
  }

  @screen md {
    @apply gap-8;
    grid-template-columns: repeat(2, minmax(0, calc(100% / 3)));

    &--4\/4\/4 {
      grid-template-columns: repeat(3, minmax(0, calc(100% / 3)));
    }
  }
}
</style>
